/**
 * One write on a scenario's path (board 3): whether it is Ready on this scenario's own run of approvals, its
 * auto-approve switch, and every reject with who, where, when and their note.
 */

import { Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import type { LiveDecision, LiveWrite } from '../types';
import { formatWhen, percentOf, runLineOf } from './scenarios';
import { useDecisions } from './use-live-promote';
import { sourceLabelOf } from './view-rules';

const RejectRow = ({ decision }: { decision: LiveDecision }) => (
	<li className='flex flex-col gap-0.5 border-b border-gray-300 px-3 py-2 last:border-b-0'>
		<span className='flex justify-between gap-2 text-xs'>
			<span className='font-medium text-text-heading'>Ticket {decision.ticketId}</span>
			<span className='text-gray-800'>
				{[decision.decidedBy ?? 'unknown', sourceLabelOf(decision.source), formatWhen(decision.decidedAt)].join(' · ')}
			</span>
		</span>
		<span className='text-[13px] text-gray-900'>
			{decision.notes ? `"${decision.notes}"` : <span className='text-gray-800'>No note</span>}
		</span>
	</li>
);

const Rejects = ({ sopId, typeId, tool }: { sopId: string; typeId: string; tool: string }) => {
	const { data, isPending, error } = useDecisions(sopId, typeId, tool);
	if (isPending) {
		return (
			<p className='flex items-center gap-2 text-xs text-gray-800'>
				<Loader2 className='size-3.5 animate-spin' aria-hidden /> Reading the rejects…
			</p>
		);
	}
	if (error) return <p className='text-xs text-gray-800'>Could not read the rejects.</p>;
	const rejects = (data ?? []).filter((decision) => decision.decision === 'reject');
	if (!rejects.length) return null;
	return (
		<ul className='rounded-xl border border-gray-300' aria-label={`Rejects of ${tool}`}>
			{rejects.map((decision) => (
				<RejectRow key={`${decision.ticketId}-${decision.decidedAt}`} decision={decision} />
			))}
		</ul>
	);
};

interface WriteActionProps {
	sopId: string;
	typeId: string;
	scenario: string;
	write: LiveWrite;
	label: string;
	/** A reject alert went out for this write in this scenario. */
	alertSent: boolean;
	/** Why auto-approve cannot be switched on (the reply drafts); null when it can. */
	blockedReason: string | null;
	canEdit: boolean;
	busy: boolean;
	onAutoApprove: (on: boolean) => void;
}

export const WriteAction = ({
	sopId,
	typeId,
	scenario,
	write,
	label,
	alertSent,
	blockedReason,
	canEdit,
	busy,
	onAutoApprove
}: WriteActionProps) => {
	const decided = write.approved + write.rejected;
	const rate = percentOf(write.rejectRate);
	const auto = write.mode === 'auto';
	return (
		<div className='flex flex-col gap-1.5'>
			<div className='flex items-center justify-between gap-2.5'>
				<span className='flex items-center gap-2'>
					<span className='rounded-md bg-gray-100 px-1.5 text-[13px] font-medium text-text-heading'>{label}</span>
					<Badge variant={write.ready ? 'success' : 'warning'}>{write.ready ? 'Ready' : 'Not ready'}</Badge>
				</span>
				<label className='flex items-center gap-2 text-[13px] text-gray-900'>
					Auto-approve
					<Switch
						checked={auto}
						disabled={!canEdit || busy || (!auto && Boolean(blockedReason))}
						onCheckedChange={onAutoApprove}
						aria-label={`Auto-approve ${label} in ${scenario}`}
					/>
				</label>
			</div>
			<span className='text-xs text-gray-800'>
				{auto ? 'On: it runs without a supervisor.' : 'Off: a supervisor approves each run.'} {runLineOf(write)} in this
				scenario. A rejection here starts the count again; one in another scenario doesn't.
			</span>
			{!auto && blockedReason && <span className='text-xs text-gray-800'>{blockedReason}</span>}
			{decided > 0 && (
				<div className='mt-1 flex items-center justify-between text-[13px] text-gray-900'>
					<span>
						Rejected {write.rejected} of {decided} times in 30 days
					</span>
					{write.rejected > 0 && rate && (
						<Badge variant='destructive'>
							{rate}
							{alertSent ? ' · alert sent' : ''}
						</Badge>
					)}
				</div>
			)}
			{write.rejected > 0 && <Rejects sopId={sopId} typeId={typeId} tool={write.tool} />}
		</div>
	);
};
